/**
 * TagPicker — relay-66 inline `#` tag picker for the composer.
 *
 * Anchors above the textarea. Surfaces standard Charter v12 tag
 * vocabulary plus the user's recently-used tags (workspace-scoped),
 * filtered by the active query. Mirrors `MentionPicker`'s shape and
 * keyboard handling so both pickers feel identical to the user.
 *
 * Selection: calls `onSelect(tag)`. The composer is responsible for
 * removing the `#query` text from the textarea and adding the tag to
 * its tag-chip array.
 *
 * Free-form: when the user types a `#newtag` that doesn't match any
 * standard or recent entry, the picker still surfaces a "Use #newtag"
 * row at the bottom so unknown tags can be created on the fly.
 */

import { useEffect, useMemo, useState } from "react";
import { fuzzyFilterTags, STANDARD_TAGS } from "../lib/tagRegistry";
import { getTagCountSync, loadTagCounts } from "../lib/tagCounts";

interface Props {
  /** Recently-used tags (workspace-scoped, recency-ordered, max 20). */
  recents: string[];
  /** Workspace whose tag usage counts are shown (loaded when the picker opens). */
  workspaceId?: string;
  /** Current query string (what the user has typed after `#`). */
  query: string;
  onSelect: (tag: string) => void;
  onClose: () => void;
  /**
   * seq #22348 — hides "directive" from suggestions and blocks it as a
   * freeform pick for anyone but a verified workspace lead. Advisory only:
   * the server (assertDirectiveAuthorized, service.ts) unconditionally
   * rejects a directive-tagged post from anyone else regardless of what
   * the client sends — this just keeps the picker from offering a tag
   * whose post is guaranteed to be rejected.
   */
  isHumanLead?: boolean;
}

const FREEFORM_OK_RE = /^[A-Za-z0-9_:-]+$/;
const LEAD_ONLY_TAGS = new Set(["directive"]);

/**
 * seq #22348 — true when `tag` is restricted to a verified workspace lead
 * and the caller isn't one. Exported (pure, no hooks) so the restriction
 * itself is unit-testable without rendering the picker.
 */
export function isLeadOnlyTag(tag: string, isHumanLead: boolean): boolean {
  return !isHumanLead && LEAD_ONLY_TAGS.has(tag.toLowerCase());
}

export function TagPicker({ recents, workspaceId = "default", query, onSelect, onClose, isHumanLead = false }: Props) {
  const filtered = useMemo(() => {
    const all = fuzzyFilterTags(query, STANDARD_TAGS, recents);
    return all.filter((t) => !isLeadOnlyTag(t, isHumanLead));
  }, [query, recents, isHumanLead]);
  const showFreeform = useMemo(() => {
    if (query.length === 0) return false;
    if (!FREEFORM_OK_RE.test(query)) return false;
    if (isLeadOnlyTag(query, isHumanLead)) return false;
    return !filtered.some((t) => t.toLowerCase() === query.toLowerCase());
  }, [query, filtered, isHumanLead]);

  const items = useMemo(() => {
    return showFreeform ? [...filtered, `__create:${query}`] : filtered;
  }, [filtered, showFreeform, query]);

  const [highlightIdx, setHighlightIdx] = useState(0);

  // Tag counts load when the picker opens (not at app start) for every tag it
  // can offer, independent of the typed query so typing never refetches.
  const [, setCountsVersion] = useState(0);
  const countKey = recents.join("\u0000");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const changed = await loadTagCounts(workspaceId, [...recents, ...STANDARD_TAGS]);
        if (changed && !cancelled) setCountsVersion((v) => v + 1);
      } catch (err) {
        console.warn("[TagPicker] tag counts unavailable:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, countKey]);

  useEffect(() => {
    setHighlightIdx((prev) => (items.length === 0 ? 0 : Math.min(prev, items.length - 1)));
  }, [items.length]);

  // Window-level capture-phase listener — same pattern as MentionPicker
  // so the picker beats the textarea's onKeyDown for ↑/↓/Enter/Tab/Esc.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (items.length === 0 && e.key !== "Escape") return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const pick = items[highlightIdx];
        if (!pick) return;
        const tag = pick.startsWith("__create:") ? pick.slice("__create:".length) : pick;
        onSelect(tag);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [items, highlightIdx, onSelect, onClose]);

  if (items.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Pick a tag"
      style={{
        position: "absolute",
        bottom: "calc(100% + 4px)",
        left: 0,
        zIndex: 11,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: 4,
        minWidth: 200,
        maxHeight: 240,
        overflowY: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {items.map((entry, i) => {
        const isCreate = entry.startsWith("__create:");
        const display = isCreate ? entry.slice("__create:".length) : entry;
        const count = isCreate ? null : getTagCountSync(display);
        const isActive = i === highlightIdx;
        return (
          <button
            key={`${entry}:${i}`}
            type="button"
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => setHighlightIdx(i)}
            onClick={() => onSelect(display)}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              background: isActive ? "var(--surface2)" : "transparent",
              color: isCreate ? "var(--text2)" : "var(--text)",
              border: "none",
              padding: "6px 10px",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
              borderRadius: 4,
              fontStyle: isCreate ? "italic" : "normal",
            }}
          >
            <span>{isCreate ? `Use #${display}` : `#${display}`}</span>
            {!isCreate && count != null && count > 0 && (
              <span
                style={{
                  fontSize: 10,
                  color: "var(--text2)",
                  marginLeft: 8,
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
